import React, { useState } from "react";
import {
  Box,
  InputAdornment,
  Stack,
  TextField,
  Typography,
  Skeleton,
} from "@mui/material";
import RecentTableContent from "features/Collections/components/RecentTable";
import useRecentCollection from "features/Collections/hooks/use-recent-collection.hook";
import CollectionCard from "features/Collections/components/CollectionCard";
import { fillParent, scrollingBox } from "utils/base-styles";
import Footer from "components/layout/Footer";
import { Swiper, SwiperSlide } from "swiper/react";
import { Parallax, Pagination, Mousewheel } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import "./styles.css";
import { useIsMobile } from "hooks/use-breakpoint-checks.hook";
import PageTitle from "components/layout/PageTitle";
import { Helmet } from "react-helmet-async";

export default function RecentsCollections() {
  const [filter, setFilter] = useState("");

  const { filteredRecentCollection, recentCollections, isLoading } =
    useRecentCollection(filter);

  const isMobile = useIsMobile();

  return (
    <Box
      sx={{
        ...fillParent,
        ...scrollingBox,
      }}
    >
      <Helmet>
        <title>Sooth - Collections</title>
      </Helmet>
      <Stack direction="column" gap={4}>
        <Stack
          mt={3}
          justifyContent="space-between"
          alignItems="center"
          direction="row"
        >
          <PageTitle alignItems="center" variant="h3" sx={{ mt: 0 }}>
            {filter ? "Search Bookmarks" : "Recent Activity"}
          </PageTitle>
          <TextField
            onChange={(e) => setFilter(e.target.value)}
            sx={{ width: { xs: "50%", sm: "30%" } }}
            value={filter}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ height: 30 }} />
                </InputAdornment>
              ),
              endAdornment: filter && (
                <InputAdornment position="end">
                  <ClearIcon
                    sx={{ cursor: "pointer", height: 30 }}
                    onClick={() => setFilter("")}
                  />
                </InputAdornment>
              ),
            }}
            placeholder="Search Collections"
          />
        </Stack>
        {!filter && (
          <Box sx={{ mb: { xs: 2, sm: 0 } }}>
            {!isMobile ? (
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: {
                    sm: "repeat(2, 1fr)",
                    md: "repeat(4, 1fr)",
                  },
                  overflowX: "auto",
                  pb: 1
                }}
              >
                {(isLoading ? Array.from({ length: 4 }) : recentCollections).map(
                  (collection, index) =>
                    isLoading ? (
                      <Box key={`skeleton-${index}`}>
                        <Skeleton
                          variant="rectangular"
                          height={180}
                          sx={{ borderRadius: 3 }}
                        />
                      </Box>
                    ) : (
                      <Box key={collection.collectionID_ids || index}>
                        <CollectionCard collection={collection} />
                      </Box>
                    )
                )}
              </Box>
            ) : (
              <Box sx={{ position: "relative" }}>
                {isLoading ? (
                  <Stack direction="row" gap={2}>
                    {Array.from({ length: 3 }).map((_, idx) => (
                      <Skeleton
                        key={`m-skeleton-${idx}`}
                        variant="rectangular"
                        height={160}
                        width="40vw"
                        sx={{ borderRadius: 3, flexShrink: 0 }}
                      />
                    ))}
                  </Stack>
                ) : (
                  <>
                    <Swiper
                      modules={[Parallax, Pagination, Mousewheel]}
                      spaceBetween={30}
                      slidesPerView={2}
                      pagination={{ clickable: true, el: ".swiper-pagination" }}
                      mousewheel
                      parallax
                      speed={600}
                      style={{ marginTop: "20px" }}
                    >
                      {recentCollections.map((collection, index) => (
                        <SwiperSlide key={collection.collectionID_ids || index}>
                          <CollectionCard collection={collection} />
                        </SwiperSlide>
                      ))}
                    </Swiper>
                    <div className="swiper-pagination" />
                  </>
                )}
              </Box>
            )}
          </Box>
        )}
        {!filter && <Typography variant="h4">Recent Bookmarks</Typography>}
        <RecentTableContent
          recentCollectionSource={filteredRecentCollection}
          loading={isLoading}
          filter={filter}
        />
      </Stack>
      <Footer sx={{ px: 0 }} />
    </Box>
  );
}
